import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface ClientModule {
  id: string;
  client_id: string;
  module_code: string;
  is_active: boolean;
  activated_at: string;
  deactivated_at: string | null;
  source: string;
}

const AVAILABLE_MODULES = [
  { code: 'incidents', name: 'Incidentes', description: 'Gerenciamento de incidentes' },
  { code: 'requests', name: 'Requisições', description: 'Requisições de serviço' },
  { code: 'problems', name: 'Problemas', description: 'Gerenciamento de problemas' },
  { code: 'changes', name: 'Mudanças', description: 'Controle de mudanças' },
  { code: 'knowledge_base', name: 'Base de Conhecimento', description: 'Artigos e documentação' },
  { code: 'forms', name: 'Formulários', description: 'Formulários personalizados' },
  { code: 'reports', name: 'Relatórios', description: 'Relatórios e dashboards' },
  { code: 'asset_management', name: 'Gestão de Ativos', description: 'Inventário de ativos' },
  { code: 'software_management', name: 'Gestão de Software', description: 'Licenças de software' },
  { code: 'sla_management', name: 'Gestão de SLA', description: 'Acordos de nível de serviço' },
];

export const useClientModulesAdmin = (clientId?: string) => {
  const queryClient = useQueryClient();

  const { data: modules = [], isLoading, error } = useQuery({
    queryKey: ['client-modules-admin', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('client_id', clientId);

      if (error) throw error;
      return data as ClientModule[];
    },
    enabled: !!clientId,
  });

  const activateModuleMutation = useMutation({
    mutationFn: async ({ clientId, moduleCode }: { clientId: string; moduleCode: string }) => {
      // Check if module exists but is inactive
      const { data: existing } = await supabase
        .from('client_modules')
        .select('id')
        .eq('client_id', clientId)
        .eq('module_code', moduleCode)
        .single();

      if (existing) {
        // Reactivate existing module
        const { error } = await supabase
          .from('client_modules')
          .update({
            is_active: true,
            activated_at: new Date().toISOString(),
            deactivated_at: null,
            source: 'admin_backoffice',
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Create new module
        const { error } = await supabase
          .from('client_modules')
          .insert({
            client_id: clientId,
            module_code: moduleCode,
            is_active: true,
            activated_at: new Date().toISOString(),
            source: 'admin_backoffice',
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-modules-admin'] });
      toast({ title: 'Módulo ativado', description: 'Módulo ativado com sucesso' });
    },
    onError: (error) => {
      console.error('Error activating module:', error);
      toast({ title: 'Erro', description: 'Erro ao ativar módulo', variant: 'destructive' });
    },
  });

  const deactivateModuleMutation = useMutation({
    mutationFn: async ({ clientId, moduleCode }: { clientId: string; moduleCode: string }) => {
      const { error } = await supabase
        .from('client_modules')
        .update({
          is_active: false,
          deactivated_at: new Date().toISOString(),
        })
        .eq('client_id', clientId)
        .eq('module_code', moduleCode);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-modules-admin'] });
      toast({ title: 'Módulo desativado', description: 'Módulo desativado com sucesso' });
    },
    onError: (error) => {
      console.error('Error deactivating module:', error);
      toast({ title: 'Erro', description: 'Erro ao desativar módulo', variant: 'destructive' });
    },
  });

  const toggleModule = async (clientId: string, moduleCode: string, activate: boolean) => {
    if (activate) {
      await activateModuleMutation.mutateAsync({ clientId, moduleCode });
    } else {
      await deactivateModuleMutation.mutateAsync({ clientId, moduleCode });
    }
  };

  const getActiveModuleCodes = () => {
    return modules.filter(m => m.is_active).map(m => m.module_code);
  };

  const isModuleActive = (moduleCode: string) => {
    return modules.some(m => m.module_code === moduleCode && m.is_active);
  };

  return {
    modules,
    isLoading,
    error,
    activateModule: activateModuleMutation.mutate,
    deactivateModule: deactivateModuleMutation.mutate,
    toggleModule,
    getActiveModuleCodes,
    isModuleActive,
    availableModules: AVAILABLE_MODULES,
    isActivating: activateModuleMutation.isPending,
    isDeactivating: deactivateModuleMutation.isPending,
  };
};
